import './kp-pdf-page.less';

import type {PDFPageProxy, RenderTask} from 'pdfjs-dist/types/src/display/api';
import type {IAugmentedJQuery, IDoCheck, IOnInit, IScope, IWindowService} from 'angular';
import type {KpPdfScrollingPageComponentController} from '../kp-scrolling-page/kp-pdf-scrolling-page.component';
import KpPdfScrollingPageComponent from '../kp-scrolling-page/kp-pdf-scrolling-page.component';
import type {PdfScale} from '../kp-pdf-viewer/kp-pdf-viewer.component';
import type {Component, OnChanges, OnChangesObject} from 'typings/portaro.fe.types';
import type {PdfError, PdfInfo} from '../pdf-viewer';
import type {PageViewport} from 'pdfjs-dist/types/src/display/display_utils';

export abstract class KpPdfPageComponentController implements KpPdfPageComponentBindings, IOnInit, OnChanges<KpPdfPageComponentBindings>, IDoCheck {
    public page: PDFPageProxy;
    public scale: PdfScale;
    public optimalScale: number;
    public isPageFocused: boolean;
    public isElementFocused: boolean;

    public abstract onPageFocus(locals: { $pageNumber: number });

    public abstract onPageRendered(locals: { $info: any });

    public abstract onPageErrored(locals: { $error: any });

    public rendered: boolean;
    private viewport: PageViewport;
    private renderTask: RenderTask;
    private pdfScrollingPageController: KpPdfScrollingPageComponentController;
    private isElementFocusedOld: boolean;

    /*@ngInject*/
    constructor(private $element: IAugmentedJQuery, private $window: IWindowService, private $scope: IScope) {
    }

    public $onInit(): void {
        this.setViewport();
        this.rendered = false;
    }

    public $onChanges(onChangesObj: OnChangesObject<KpPdfPageComponentBindings>): void {
        if (onChangesObj.scale && onChangesObj.scale.currentValue) {
            this.updateVisibility();
        }

        if (onChangesObj.optimalScale && onChangesObj.optimalScale.currentValue) {
            this.setViewport();
        }
    }

    public $doCheck(): void {
        if (this.pdfScrollingPageController.isElementFocused !== this.isElementFocusedOld) {
            this.isElementFocusedOld = this.pdfScrollingPageController.isElementFocused;
            if (this.pdfScrollingPageController.isElementFocused) {
                this.focusPage();
            }
        }
    }

    private setViewport() {
        this.viewport = this.page.getViewport({scale: this.optimalScale});
    }

    private focusPage() {
        if (this.pdfScrollingPageController.isPageFocused) {
            return;
        }

        this.onPageFocus({$pageNumber: this.pageNumber});
    }

    private async drawPage() {
        if (this.renderTask) {
            return;
        }

        const {viewport} = this;
        const canvasContext = this.canvas.getContext('2d');
        const renderContext = {canvasContext, viewport};

        // PDFPageProxy#render
        // https://mozilla.github.io/pdf.js/api/draft/PDFPageProxy.html
        this.renderTask = this.page.render(renderContext);

        try {
            await this.renderTask.promise;

            this.rendered = true;

            this.$scope.$applyAsync();

            this.onPageRendered({
                $info: {
                    page: this.page,
                    text: `Rendered page ${this.pageNumber}`
                }
            });

        } catch (error) {
            this.destroyRenderTask();
            this.onPageErrored({
                $error: {
                    error,
                    page: this.page,
                    text: `Failed to render page ${this.pageNumber}`
                }
            });
        }
    }

    private updateVisibility() {
        this.pdfScrollingPageController.updateElementBounds();
    }

    private destroyPage(page: PDFPageProxy) {
        if (page) {
            // @ts-ignore Bad typing. PDFPageProxy#_destroy - https://mozilla.github.io/pdf.js/api/draft/PDFPageProxy.html
            page._destroy();
        }

        this.destroyRenderTask();
    }

    private destroyRenderTask() {
        if (!this.renderTask) {
            return;
        }

        this.renderTask.cancel();
        this.renderTask = null;
    }

    public get canvasStyle(): string {
        const {width: actualSizeWidth, height: actualSizeHeight} = this.actualSizeViewport;
        const pixelRatio = this.$window.devicePixelRatio || 1;
        const [pixelWidth, pixelHeight] = [actualSizeWidth, actualSizeHeight]
            .map((dimension) => Math.ceil(dimension / pixelRatio));

        return `width: ${pixelWidth}px; height: ${pixelHeight}px;`;
    }

    public get canvasWidth(): number {
        return Math.ceil(this.viewport.width) || 0;
    }

    public get canvasHeight(): number {
        return Math.ceil(this.viewport.height) || 0;
    }

    private get actualSizeViewport() {
        // @ts-ignore We can omit other fields.
        return this.viewport.clone({scale: this.scale.value});
    }

    private get pageNumber(): number {
        return this.page.pageNumber;
    }

    private get canvas(): HTMLCanvasElement {
        return this.$element[0].querySelector('canvas');
    }
}

export default class KpPdfPageComponent implements Component<KpPdfPageComponentBindings> {
    public static componentName = 'kpPdfPage';

    public template = `<canvas 
                            class="pdf-page box-shadow" 
                            width="{{pdfPageController.canvasWidth}}" 
                            height="{{pdfPageController.canvasHeight}}"
                            style="{{pdfPageController.canvasStyle}}"
                            
                            ng-show="pdfPageController.rendered"
                        >
                        </canvas>
                        <div 
                            class="pdf-page-spinner"
                            style="{{pdfPageController.canvasStyle}}"
                            ng-hide="pdfPageController.rendered"  
                            in-view="$inview && pdfPageController.drawPage()"
                        >
                            <jp-loading></jp-loading> 
                        </div>`;

    public bindings = {
        page: '<',
        scale: '<',
        optimalScale: '<',
        isPageFocused: '<?',
        isElementFocused: '<?',
        onPageFocus: '&',
        onPageRendered: '&',
        onPageErrored: '&'
    } as const;
    public controller = KpPdfPageComponentController;
    public require = {
        pdfScrollingPageController: `^${KpPdfScrollingPageComponent.componentName}`
    };
    public controllerAs = 'pdfPageController';
}

interface KpPdfPageComponentBindings {
    page: PDFPageProxy;
    scale: PdfScale;
    optimalScale: number;
    isPageFocused?: boolean;
    isElementFocused?: boolean;

    onPageFocus(locals: { $pageNumber: number });

    onPageRendered(locals: { $info: PdfInfo });

    onPageErrored(locals: { $error: PdfError });
}
